// Arquivo: useAuthStatus.ts

import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { fetchUserProfile } from '../lib/authUtils';
import { useAuthStore } from '../lib/store';

export function useAuthStatus() {
  // Seletores do store Zustand
  const user = useAuthStore((state) => state.user);
  const userProfile = useAuthStore((state) => state.userProfile);
  const isLoading = useAuthStore((state) => state.isLoading);
  const setUser = useAuthStore((state) => state.setUser);
  const setUserProfile = useAuthStore((state) => state.setUserProfile);
  const setIsLoading = useAuthStore((state) => state.setIsLoading);
  const isAdmin = useAuthStore((state) => state.isAdmin);
  const isSuperAdmin = useAuthStore((state) => state.isSuperAdmin);
  const isManager = useAuthStore((state) => state.isManager);
  const setAccessibleComponents = useAuthStore((state) => state.setAccessibleComponents);

  const authListenerRef = useRef<{
    subscription?: { unsubscribe: () => void };
  } | null>(null);

  // Efeito para sessão inicial e listener de autenticação
  useEffect(() => {
    let mounted = true;
    setIsLoading(true);

    const checkInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (!mounted) return;

        if (error) {
          console.error('[useAuthStatus Efeito 1] Erro na sessão inicial:', error);
          if (useAuthStore.getState().user !== null) setUser(null);
          if (useAuthStore.getState().userProfile !== null) setUserProfile(null);
        } else if (session?.user) {
          const currentUserInStore = useAuthStore.getState().user;
          const newUserFromSession = session.user;
          if (!currentUserInStore || currentUserInStore.id !== newUserFromSession.id) {
            setUser(newUserFromSession);
          } else if (currentUserInStore.email !== newUserFromSession.email || 
                     JSON.stringify(currentUserInStore.user_metadata) !== JSON.stringify(newUserFromSession.user_metadata)) {
            setUser(newUserFromSession);
          }
        } else {
          if (useAuthStore.getState().user !== null) {
            setUser(null);
            setUserProfile(null);
          }
        }
      } catch (catchError) {
        console.error('[useAuthStatus Efeito 1] Exceção na sessão inicial:', catchError);
        if (mounted) {
          setUser(null);
          setUserProfile(null);
        }
      }
    };

    checkInitialSession();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;

        const currentUserInStore = useAuthStore.getState().user;
        const newUser = session?.user ?? null;

        let userChanged = false;

        switch (event) {
          case 'INITIAL_SESSION':
          case 'SIGNED_IN':
            if (newUser && (!currentUserInStore || currentUserInStore.id !== newUser.id)) {
              setUser(newUser);
              userChanged = true;
            } else if (newUser && currentUserInStore && currentUserInStore.id === newUser.id) {
              if (currentUserInStore.email !== newUser.email || 
                  JSON.stringify(currentUserInStore.user_metadata) !== JSON.stringify(newUser.user_metadata)) {
                setUser(newUser);
                userChanged = true;
              }
            } else if (!newUser && currentUserInStore) {
              setUser(null);
              setUserProfile(null);
              userChanged = true;
            }
            break;

          case 'TOKEN_REFRESHED':
          case 'USER_UPDATED':
            if (newUser && currentUserInStore) {
              if (currentUserInStore.id === newUser.id) {
                if (currentUserInStore.email !== newUser.email || 
                    JSON.stringify(currentUserInStore.user_metadata) !== JSON.stringify(newUser.user_metadata)) {
                  setUser(newUser);
                  userChanged = true;
                }
              } else { 
                console.warn(`[Auth Listener] Evento ${event}: ID do usuário mudou inesperadamente. Tratando como novo login. New ID: ${newUser.id}, Old ID: ${currentUserInStore.id}`);
                setUser(newUser);
                userChanged = true;
              }
            } else if (newUser && !currentUserInStore) {
              setUser(newUser);
              userChanged = true;
            } else if (!newUser && currentUserInStore) {
              setUser(null);
              setUserProfile(null);
              userChanged = true;
            }
            break;

          case 'SIGNED_OUT':
          case 'USER_DELETED':
            if (currentUserInStore) {
                setUser(null);
                setUserProfile(null);
                userChanged = true;
            }
            break;
          default:
            break;
        }

        if (!userChanged && !newUser && mounted && useAuthStore.getState().isLoading) {
            setIsLoading(false);
        }
      }
    );
    authListenerRef.current = authListener;

    return () => {
      mounted = false;
      if (authListenerRef.current?.subscription) {
        authListenerRef.current.subscription.unsubscribe();
      }
      authListenerRef.current = null;
    };
  }, [setUser, setUserProfile, setIsLoading]);

  // Efeito para buscar o perfil do usuário
  useEffect(() => {
    let mounted = true;

    const fetchProfileForCurrentUser = async () => {
      const currentProfileInStore = useAuthStore.getState().userProfile;

      if (user && (!currentProfileInStore || currentProfileInStore.user_id !== user.id)) {
        if (mounted) setIsLoading(true);
        
        try {
            const profile = await fetchUserProfile(user.id);
            if (mounted) {
                if (useAuthStore.getState().user?.id === user.id) {
                    setUserProfile(profile);
                    
                    // Agora verifica a role para decidir se deve buscar componentes acessíveis
                    if (profile?.role) {
                        const roleName = profile.role.role_name;
                        // console.log(`[useAuthStatus] User role: ${roleName}`);
                        
                        // Se for Admin ou Manager, busca componentes acessíveis
                        // Super Admin tem acesso a tudo, não precisa buscar
                        if (roleName === 'Admin' || roleName === 'Manager') {
                            await fetchAccessibleComponents(user.id);
                        } else if (roleName === 'Super Admin') {
                            // Super Admin tem acesso a tudo, definimos uma lista vazia
                            // já que o hasAccess vai retornar true independentemente
                            setAccessibleComponents([]);
                        }
                    }
                }
            }
        } catch(error) {
            console.error(`[useAuthStatus Efeito 2] Erro ao buscar perfil ou componentes para ${user.id}:`, error);
            if (mounted) setUserProfile(null);
        } finally {
            if (mounted) {
                setIsLoading(false);
            }
        }
      } else if (user && currentProfileInStore?.user_id === user.id) {
        if (useAuthStore.getState().isLoading && mounted) {
          setIsLoading(false);
        }
      } else if (!user) {
        if (currentProfileInStore && mounted) {
          setUserProfile(null);
        }
        if (useAuthStore.getState().isLoading && mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchProfileForCurrentUser();

    return () => {
      mounted = false;
    };
  }, [user, setUserProfile, setIsLoading, setAccessibleComponents]);

  // Função para buscar componentes acessíveis
  const fetchAccessibleComponents = async (userId: string) => {
    try {
      // console.log(`[fetchAccessibleComponents] Fetching components for user ${userId}`);
      
      // Primeiro, buscamos os componentes disponíveis para verificar se eles existem
      const { data: componentsData, error: componentsError } = await supabase
        .from('admin_components')
        .select('id, path');
        
      if (componentsError) {
        console.error('[fetchAccessibleComponents] Error fetching available components:', componentsError);
      } else {
        // console.log('[fetchAccessibleComponents] Available components:', componentsData);
      }
      
      // Agora buscamos os acessos do usuário
      const { data, error } = await supabase
        .from('admin_component_access')
        .select('component_id, admin_components(id, path)')
        .eq('user_id', userId);
      
      if (error) {
        console.error('[fetchAccessibleComponents] Error fetching component access:', error);
        throw error;
      }
      
      // console.log('[fetchAccessibleComponents] Raw component access data:', data);
      
      // Verificamos se temos resultados válidos
      if (!data || data.length === 0) {
        // console.log(`[fetchAccessibleComponents] No component access found for user ${userId}`);
        setAccessibleComponents([]);
        return;
      }
      
      // Extraímos os caminhos dos componentes acessíveis
      const accessiblePaths = data
        .filter(item => item.admin_components && item.admin_components.path)
        .map(item => item.admin_components.path);
      
      // console.log(`[fetchAccessibleComponents] Extracted ${accessiblePaths.length} accessible paths:`, accessiblePaths);
      
      // Se o usuário tem acesso a componentes, vamos garantir que eles também tenham acesso ao dashboard
      if (accessiblePaths.length > 0 && !accessiblePaths.includes('/admin')) {
        accessiblePaths.push('/admin');
      }
      
      setAccessibleComponents(accessiblePaths);
    } catch (error) {
      console.error(`[fetchAccessibleComponents] Erro ao buscar componentes para ${userId}:`, error);
      setAccessibleComponents([]);
    }
  };

  const handleLogout = useCallback(async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signOut({ scope: 'local' })
      if (error) {
        console.error('[useAuthStatus] Erro durante signOut:', error);
      }
    } catch (error) {
      console.error('[useAuthStatus] Exceção durante signOut:', error);
    } 
  }, [setIsLoading]);

  return {
    isAuthenticated: !!user,
    user,
    userProfile,
    isAdmin,
    isSuperAdmin,
    isManager,
    isLoading,
    handleLogout,
  };
}